import React, { useState, useEffect } from 'react';
import { FileList, FileEditor, Header, BackupManager, Login } from './components';
import { JsonFileService, AuthService, BackupService, ImageService, setUnauthorizedCallback } from './services';
import { JsonFile } from './types';

// Crear instancias de servicios fuera del componente para evitar recreaciones
const authService = new AuthService();
const jsonFileService = new JsonFileService();
const backupService = new BackupService();
const imageService = new ImageService();

function App() {
  const [files, setFiles] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<JsonFile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isBackupManagerOpen, setIsBackupManagerOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Configurar callback para manejar tokens expirados (error 401)
    setUnauthorizedCallback(() => {
      handleLogout();
    });

    // Verificar si el usuario está autenticado
    const token = authService.getToken();
    if (token) {
      setIsAuthenticated(true);
      // Configurar token en los servicios
      jsonFileService.setAuthToken(token);
      backupService.setAuthToken(token);
      imageService.setAuthToken(token);
      loadFiles();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const filesList = await jsonFileService.getFiles();
      setFiles(filesList);
    } catch (err) {
      setError('Error al cargar la lista de archivos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadFileContent = async (filename: string) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const content = await jsonFileService.getFile(filename);
      setFileContent(content);
      setSelectedFile(filename);
    } catch (err) {
      setError(`Error al cargar el archivo ${filename}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveFile = async (content: any) => {
    if (!selectedFile) return;

    try {
      setLoading(true);
      setError(null);
      await jsonFileService.updateFile(selectedFile, content);
      setSuccess('Archivo guardado exitosamente');
      setFileContent(prev => prev ? { ...prev, content } : null);
    } catch (err) {
      setError('Error al guardar el archivo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createNewFile = async (filename: string, content: any) => {
    try {
      setLoading(true);
      setError(null);
      await jsonFileService.createFile(filename, content);
      setSuccess(`Archivo ${filename} creado exitosamente`);
      await loadFiles();
      await loadFileContent(filename);
    } catch (err) {
      setError('Error al crear el archivo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async (filename: string) => {
    try {
      setLoading(true);
      setError(null);
      await jsonFileService.deleteFile(filename);
      setSuccess(`Archivo ${filename} eliminado exitosamente`);
      await loadFiles();
      if (selectedFile === filename) {
        setSelectedFile(null);
        setFileContent(null);
      }
    } catch (err) {
      setError('Error al eliminar el archivo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleRestoreFromBackup = (filename: string) => {
    setSelectedFile(filename);
    loadFileContent(filename);
    setSuccess(`Archivo ${filename} restaurado desde backup`);
  };

  const handleLoginSuccess = () => {
    const token = authService.getToken();
    if (token) {
      setIsAuthenticated(true);
      jsonFileService.setAuthToken(token);
      backupService.setAuthToken(token);
      imageService.setAuthToken(token);
      loadFiles();
    }
  };

  const handleLogout = () => {
    authService.removeToken();
    jsonFileService.removeAuthToken();
    backupService.removeAuthToken();
    imageService.removeAuthToken();
    setIsAuthenticated(false);
    setSelectedFile(null);
    setFileContent(null);
    setFiles([]);
    setError(null);
    setSuccess(null);
  };

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="App">
      <Header onLogout={handleLogout} />
      <div className="container">
        {error && (
          <div className="error">
            {error}
            <button onClick={clearMessages} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>
              ×
            </button>
          </div>
        )}
        {success && (
          <div className="success">
            {success}
            <button onClick={clearMessages} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>
              ×
            </button>
          </div>
        )}
        
        <div className={`main-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <FileList
            files={files}
            selectedFile={selectedFile}
            onFileSelect={loadFileContent}
            onRefresh={loadFiles}
            onCreateFile={createNewFile}
            onDeleteFile={deleteFile}
            loading={loading}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={toggleSidebar}
            onOpenBackups={() => setIsBackupManagerOpen(true)}
          />
          
          <FileEditor
            selectedFile={selectedFile}
            fileContent={fileContent}
            onSave={saveFile}
            loading={loading}
            imageService={imageService}
          />
        </div>

        <BackupManager
          isOpen={isBackupManagerOpen}
          onClose={() => setIsBackupManagerOpen(false)}
          onRestore={handleRestoreFromBackup}
          backupService={backupService}
        />
      </div>
    </div>
  );
}

export default App;

